function initEssayCards(root) {
  if (!root || root.dataset.essayReady === 'true') return;

  const scroller = root.querySelector('[data-essay-track]');
  const prev = root.querySelector('[data-essay-prev]');
  const next = root.querySelector('[data-essay-next]');
  if (!scroller) return;

  root.dataset.essayReady = 'true';

  if (root.classList.contains('essay-cards--rows')) return;

  const isTwoRows = root.classList.contains('essay-cards--two-rows');
  const track = isTwoRows ? scroller.querySelector('.essay-cards__track') : scroller;

  const visibleColumns = () => {
    const raw = getComputedStyle(isTwoRows ? scroller : root)
      .getPropertyValue('--essay-carousel-visible')
      .trim();
    const n = parseFloat(raw);
    return Number.isFinite(n) && n > 0 ? n : 1;
  };

  const gapSize = () => {
    if (!track) return 20;
    const styles = window.getComputedStyle(track);
    return parseFloat(styles.columnGap || styles.gap) || 20;
  };

  const layoutTwoRows = () => {
    if (!isTwoRows || !track) return;
    const visible = visibleColumns();
    const gap = gapSize();
    const width = Math.max(0, (scroller.clientWidth - gap * (visible - 1)) / visible);
    track.style.gridAutoColumns = `${width}px`;
    track.style.columnGap = `${gap}px`;
  };

  const columnStep = () => {
    if (isTwoRows && track) {
      const width = parseFloat(track.style.gridAutoColumns) || 0;
      return width + gapSize();
    }
    const card = root.querySelector('.essay-card');
    if (!card) return scroller.clientWidth;
    return card.getBoundingClientRect().width + gapSize();
  };

  const maxScrollLeft = () => Math.max(0, scroller.scrollWidth - scroller.clientWidth);

  const updateArrows = () => {
    if (!prev && !next) return;
    const max = maxScrollLeft();
    const left = scroller.scrollLeft;
    if (prev) prev.disabled = left <= 2;
    if (next) next.disabled = max <= 2 || left >= max - 2;
  };

  const scrollByColumn = (dir) => {
    layoutTwoRows();
    const step = columnStep() * dir;
    if (!step) return;
    const target = Math.min(maxScrollLeft(), Math.max(0, scroller.scrollLeft + step));
    scroller.scrollTo({ left: target, behavior: 'smooth' });
    window.setTimeout(updateArrows, 350);
  };

  prev?.addEventListener('click', (event) => {
    event.preventDefault();
    scrollByColumn(-1);
  });
  next?.addEventListener('click', (event) => {
    event.preventDefault();
    scrollByColumn(1);
  });
  scroller.addEventListener('scroll', updateArrows, { passive: true });
  window.addEventListener('resize', () => {
    layoutTwoRows();
    updateArrows();
  });

  // Mouse / pointer drag to swipe.
  // Only enter drag mode after a small movement threshold so normal link clicks still work.
  let pointerActive = false;
  let isDragging = false;
  let dragMoved = false;
  let startX = 0;
  let startScroll = 0;
  let activePointerId = null;
  const dragThreshold = 6;

  const endDrag = (event) => {
    if (!pointerActive) return;
    pointerActive = false;
    isDragging = false;
    scroller.classList.remove('is-dragging');
    try {
      if (activePointerId != null) scroller.releasePointerCapture?.(activePointerId);
    } catch (e) {
      /* ignore */
    }
    activePointerId = null;
    updateArrows();

    // Clear dragMoved after the click event that follows pointerup.
    if (dragMoved) {
      window.setTimeout(() => {
        dragMoved = false;
      }, 0);
    }
  };

  const onPointerDown = (event) => {
    if (event.pointerType === 'touch') return; // native touch swipe
    if (event.button != null && event.button !== 0) return;
    // Allow normal clicks on interactive controls; the card's full-link handles navigation.
    if (event.target.closest('button, input, textarea, select, label')) return;
    pointerActive = true;
    isDragging = false;
    dragMoved = false;
    startX = event.clientX;
    startScroll = scroller.scrollLeft;
    activePointerId = event.pointerId;
  };

  const onPointerMove = (event) => {
    if (!pointerActive) return;
    const dx = event.clientX - startX;
    if (!isDragging) {
      if (Math.abs(dx) <= dragThreshold) return;
      isDragging = true;
      dragMoved = true;
      scroller.classList.add('is-dragging');
      scroller.setPointerCapture?.(activePointerId);
    }
    scroller.scrollLeft = startScroll - dx;
    updateArrows();
  };

  scroller.addEventListener('pointerdown', onPointerDown);
  scroller.addEventListener('pointermove', onPointerMove);
  scroller.addEventListener('pointerup', endDrag);
  scroller.addEventListener('pointercancel', endDrag);
  scroller.addEventListener('pointerleave', (event) => {
    if (!pointerActive || isDragging) return;
    endDrag(event);
  });

  // Prevent accidental link clicks after a drag
  scroller.addEventListener(
    'click',
    (event) => {
      if (!dragMoved) return;
      event.preventDefault();
      event.stopPropagation();
    },
    true
  );

  layoutTwoRows();
  updateArrows();
  // Re-measure after images/fonts settle
  window.setTimeout(() => {
    layoutTwoRows();
    updateArrows();
  }, 100);
}

document.querySelectorAll('[data-essay-cards]').forEach(initEssayCards);

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-essay-cards]').forEach((root) => {
    delete root.dataset.essayReady;
    initEssayCards(root);
  });
});
